// src/components/festival/BoidSimulation.jsx

import React, { useEffect, useRef, useState } from 'react';
import { COLORS } from '../shared/ContentData';
import { TonalMatrix } from '../shared/TonalMatrix';
import FestivalChatbot from './FestivalChatbot';



const layoutConfig = {
    menu: {
      left: 350,
      fontSize: 30,
      gap: 30,
    },
    shape: {
      desktop: {
        scale: 1,
        margin: 130,
        widthRatio: 0.6,
      },
      mobile: {
        scale: 0.25,  // 25% of original size
        margin: 20,   // Position from left edge
        bottom: 20,   // Position from bottom
        widthRatio: 0.6,
      }
    }
  };
  

  // Add after layoutConfig and before the PlayMode effect handler
const duskColors = [
    { r: 89, g: 97, b: 146 },    // Deep blue
    { r: 157, g: 128, b: 147 },  // Dusty purple
    { r: 221, g: 163, b: 163 },  // Soft pink
    { r: 244, g: 210, b: 194 },  // Pale orange
    { r: 89, g: 97, b: 146 }     // Back to deep blue for seamless loop
  ];
  
  // Add color interpolation function
  const interpolateColors = (colors, t) => {
    const segments = colors.length - 1;
    const segment = Math.floor(t * segments);
    const segmentT = (t * segments) % 1;
    
    const color1 = colors[segment];
    const color2 = colors[segment + 1];
    
    return {
      r: Math.round(color1.r + (color2.r - color1.r) * segmentT),
      g: Math.round(color1.g + (color2.g - color1.g) * segmentT),
      b: Math.round(color1.b + (color2.b - color1.b) * segmentT)
    };
  };

  
  

  const BoidSimulation = ({ isMobile, isPlayMode, initialBoidsState, onBoidsUpdate }) => {
    const canvasRef = useRef(null);
  const mousePosRef = useRef({ x: 0, y: 0 });
  const prevMousePosRef = useRef({ x: 0, y: 0 });
  const mouseVelocityRef = useRef({ x: 0, y: 0 });
  const lastMouseMoveTimeRef = useRef(Date.now());
  const isMouseMovingRef = useRef(false);
  const boidsRef = useRef([]);
  const requestIdRef = useRef();
  const gradientTimeRef = useRef(0);
  const tonalMatrixRef = useRef(null);
  const [boidInteraction, setBoidInteraction] = useState(false);

  function drawShape(ctx) {
    const isMobileView = window.innerWidth <= 768; // Using standard mobile breakpoint
    const config = layoutConfig.shape[isMobile ? 'mobile' : 'desktop'];
    
    const baseHeight = 320;
    const height = baseHeight * config.scale;
    const width = height * config.widthRatio;
    const lineWidth = 10 * config.scale;
    const innerOffset = lineWidth / 2;
    
    // Calculate Y position differently for mobile and desktop
    const topY = isMobileView 
      ? window.innerHeight - height - config.bottom // Position from bottom for mobile
      : (window.innerHeight - height) / 2;         // Centered for desktop
    
    const margin = config.margin;
    
    ctx.save();
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'square';
  
    // Draw outer rectangle
    ctx.beginPath();
    ctx.rect(margin, topY, width, height);
    ctx.stroke();
  
    // Draw horizontal line
    ctx.beginPath();
    ctx.moveTo(margin, topY + height/2);
    ctx.lineTo(margin + width, topY + height/2);
    ctx.stroke();
  
    // Draw diagonal lines
    ctx.beginPath();
    ctx.moveTo(margin + innerOffset, topY + height/2);
    ctx.lineTo(margin + width - innerOffset, topY + innerOffset);
    ctx.stroke();
  
    ctx.beginPath();
    ctx.moveTo(margin + innerOffset, topY + height - innerOffset);
    ctx.lineTo(margin + width - innerOffset, topY + height/2);
    ctx.stroke();
  
    ctx.restore();
  }

  class Boid {
    constructor(x, y, canvasWidth, canvasHeight, isSpecial = false) {
      this.position = { x, y };
      this.velocity = {
        x: (Math.random() * 2 - 1) * 0.5,
        y: (Math.random() * 2 - 1) * 0.5
      };
      this.acceleration = { x: 0, y: 0 };
      this.canvasWidth = canvasWidth;
      this.canvasHeight = canvasHeight;
      
      // Different configurations for mobile and desktop
      this.config = {
        mobile: {
          maxSpeed: 1.5,
          minSpeed: 0.8,
          repulsionRadius: 150,
          perceptionRadius: 40,
          separationRadius: 25,
          size: 6,
          alignmentWeight: 0.05,
          cohesionWeight: 0.01,
          separationWeight: 0.03,
        },
        desktop: {
          maxSpeed: 2.5,
          minSpeed: 1,
          repulsionRadius: 100,
          perceptionRadius: 50,
          separationRadius: 30,
          size: 8,
          alignmentWeight: 0.1,
          cohesionWeight: 0.02,
          separationWeight: 0.05,
        }
      };
  
      // Play mode modifiers
      this.playModeModifiers = {
        mobile: {
          maxSpeedMultiplier: 0.6,
          minSpeedMultiplier: 0.4,
          perceptionRadiusMultiplier: 1.4,
          separationRadiusMultiplier: 0.8,
          particleSize: { min: 2, max: 4 },
          alpha: { min: 0.4, max: 0.7 }
        },
        desktop: {
          maxSpeedMultiplier: 0.8,
          minSpeedMultiplier: 0.6,
          perceptionRadiusMultiplier: 1.4,
          separationRadiusMultiplier: 0.8,
          particleSize: { min: 2, max: 5 },
          alpha: { min: 0.5, max: 0.8 }
        }
      };
  
      this.isSpecial = isSpecial;
      this.isPlayMode = false;
      this.isMobile = false;
      this.personalityFactor = Math.random() * 0.4 + 0.8;
      
      // Initialize particle properties for play mode
      this.size = Math.random() * 3 + 2;
      this.alpha = Math.random() * 0.5 + 0.5;
    }
  
    setDeviceType(isMobile) {
      this.isMobile = isMobile;
      // Update particle properties based on device type
      const modifiers = this.playModeModifiers[isMobile ? 'mobile' : 'desktop'];
      this.size = Math.random() * (modifiers.particleSize.max - modifiers.particleSize.min) + modifiers.particleSize.min;
      this.alpha = Math.random() * (modifiers.alpha.max - modifiers.alpha.min) + modifiers.alpha.min;
    }
  
    setPlayMode(mode) {
      this.isPlayMode = mode;
    }
  
    getCurrentConfig() {
      const baseConfig = this.config[this.isMobile ? 'mobile' : 'desktop'];
      
      if (!this.isPlayMode) return baseConfig;
  
      const modifiers = this.playModeModifiers[this.isMobile ? 'mobile' : 'desktop'];
      return {
        ...baseConfig,
        maxSpeed: baseConfig.maxSpeed * modifiers.maxSpeedMultiplier,
        minSpeed: baseConfig.minSpeed * modifiers.minSpeedMultiplier,
        perceptionRadius: baseConfig.perceptionRadius * modifiers.perceptionRadiusMultiplier,
        separationRadius: baseConfig.separationRadius * modifiers.separationRadiusMultiplier,
      };
    }
  
    update(boids, mousePos) {
      const config = this.getCurrentConfig();
  
      if (!this.isPlayMode) {
        const config = layoutConfig.shape[this.isMobile ? 'mobile' : 'desktop'];
        const baseHeight = 320;
        const height = baseHeight * config.scale;
        const width = height * config.widthRatio;
        
        // Calculate Y position differently for mobile and desktop
        const topY = this.isMobile 
          ? window.innerHeight - height - config.bottom 
          : (window.innerHeight - height) / 2;
        
        const distToShape = this.getDistanceToShape(config.margin, topY, width, height);
        if (distToShape < 20 * config.scale) { // Scale the repulsion distance
          const repelX = this.position.x - (config.margin + width/2);
          const repelY = this.position.y - (topY + height/2);
          const dist = Math.sqrt(repelX * repelX + repelY * repelY);
          const repulsionStrength = (20 * config.scale - distToShape) / (20 * config.scale) * 2;
          
          this.velocity.x += (repelX / dist) * repulsionStrength;
          this.velocity.y += (repelY / dist) * repulsionStrength;
        }
      }

       // Chatbot interaction logic
  const chatbotPosition = { 
    x: window.innerWidth - 100,  // Adjust based on actual chatbot position
    y: window.innerHeight - 100  // Adjust based on actual chatbot position
  };

  const dx = this.position.x - chatbotPosition.x;
  const dy = this.position.y - chatbotPosition.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  
  // If boid is close to chatbot, trigger interaction
  if (distance < 50) {  // Adjust interaction radius as needed
    if (this.onChatbotInteraction) {
      this.onChatbotInteraction();
    }
  }

  
    
  
      // Flocking behavior
      let avgVelocityX = 0;
      let avgVelocityY = 0;
      let cohesionX = 0;
      let cohesionY = 0;
      let separationX = 0;
      let separationY = 0;
      let flockCount = 0;
  
      boids.forEach(other => {
        if (other === this) return;
        const dx = other.position.x - this.position.x;
        const dy = other.position.y - this.position.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
  
        if (distance < config.perceptionRadius) {
          avgVelocityX += other.velocity.x;
          avgVelocityY += other.velocity.y;
          cohesionX += other.position.x;
          cohesionY += other.position.y;
          flockCount++;
        }
  
        if (distance < config.separationRadius) {
          const separationFactor = (config.separationRadius - distance) / config.separationRadius;
          separationX -= dx * separationFactor;
          separationY -= dy * separationFactor;
        }
      });
  
      if (flockCount > 0) {
        avgVelocityX /= flockCount;
        avgVelocityY /= flockCount;
        cohesionX = cohesionX / flockCount - this.position.x;
        cohesionY = cohesionY / flockCount - this.position.y;
  
        this.velocity.x += (avgVelocityX - this.velocity.x) * config.alignmentWeight * this.personalityFactor;
        this.velocity.y += (avgVelocityY - this.velocity.y) * config.alignmentWeight * this.personalityFactor;
        this.velocity.x += cohesionX * config.cohesionWeight * this.personalityFactor;
        this.velocity.y += cohesionY * config.cohesionWeight * this.personalityFactor;
      }
  
      this.velocity.x += separationX * config.separationWeight;
      this.velocity.y += separationY * config.separationWeight;
  
      // Mouse interaction
      if (isMouseMovingRef.current) {
        const dxMouse = mousePos.x - this.position.x;
        const dyMouse = mousePos.y - this.position.y;
        const mouseDistance = Math.sqrt(dxMouse * dxMouse + dyMouse * dyMouse);
  
        if (mouseDistance > 0) {
          if (this.isPlayMode) {
            // Dreamy attraction to mouse in play mode
            const attractionStrength = 0.8 / (1 + mouseDistance * 0.002);
            this.velocity.x += (dxMouse / mouseDistance) * attractionStrength;
            this.velocity.y += (dyMouse / mouseDistance) * attractionStrength;
            
            // Add slight random movement for dreaminess
            this.velocity.x += (Math.random() - 0.5) * 0.01;
            this.velocity.y += (Math.random() - 0.5) * 0.01;
          } else {
            if (mouseDistance < config.repulsionRadius) {
              const repulsionStrength = (config.repulsionRadius - mouseDistance) / config.repulsionRadius;
              this.velocity.x -= (dxMouse / mouseDistance) * repulsionStrength;
              this.velocity.y -= (dyMouse / mouseDistance) * repulsionStrength;
            }
          }
        }
      }
  
      // Speed limits
      const speed = Math.sqrt(this.velocity.x * this.velocity.x + this.velocity.y * this.velocity.y);
      if (speed > config.maxSpeed) {
        this.velocity.x = (this.velocity.x / speed) * config.maxSpeed;
        this.velocity.y = (this.velocity.y / speed) * config.maxSpeed;
      } else if (speed < config.minSpeed) {
        this.velocity.x = (this.velocity.x / speed) * config.minSpeed;
        this.velocity.y = (this.velocity.y / speed) * config.minSpeed;
      }
  
      // Update position
      this.position.x += this.velocity.x;
      this.position.y += this.velocity.y;
  
      // Edge behavior
      if (this.isPlayMode) {
        // Smooth wrapping in play mode
        if (this.position.x < -50) this.position.x = this.canvasWidth + 50;
        if (this.position.x > this.canvasWidth + 50) this.position.x = -50;
        if (this.position.y < -50) this.position.y = this.canvasHeight + 50;
        if (this.position.y > this.canvasHeight + 50) this.position.y = -50;
      } else {
        // Original edge avoidance
        const edgeMargin = 10;
        const turnForce = 0.2;
  
        if (this.position.x < edgeMargin) this.velocity.x += turnForce;
        if (this.position.x > this.canvasWidth - edgeMargin) this.velocity.x -= turnForce;
        if (this.position.y < edgeMargin) this.velocity.y += turnForce;
        if (this.position.y > this.canvasHeight - edgeMargin) this.velocity.y -= turnForce;
  
        this.position.x = Math.max(0, Math.min(this.position.x, this.canvasWidth));
        this.position.y = Math.max(0, Math.min(this.position.y, this.canvasHeight));
      }
    }
  
    draw(ctx) {
      if (this.isPlayMode) {
        // Dreamy circular particles in play mode
        ctx.beginPath();
        ctx.arc(this.position.x, this.position.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.isSpecial ? 
          `rgba(255, 255, 255, ${this.alpha})` : 
          `rgba(255, 255, 255, ${this.alpha * 0.8})`;
        ctx.fill();
      } else {
        // Original triangle drawing
        const angle = Math.atan2(this.velocity.y, this.velocity.x);
        const config = this.getCurrentConfig();
  
        ctx.save();
        ctx.translate(this.position.x, this.position.y);
        ctx.rotate(angle);
  
        ctx.beginPath();
        ctx.moveTo(config.size, 0);
        ctx.lineTo(-config.size/2, config.size/2);
        ctx.lineTo(-config.size/2, -config.size/2);
        ctx.closePath();
        ctx.fillStyle = this.isSpecial ? '#ff0000' : '#016341';
        ctx.fill();
  
        ctx.restore();
      }
    }
  
    getDistanceToShape(x, y, width, height) {
      const closestX = Math.max(x, Math.min(this.position.x, x + width));
      const closestY = Math.max(y, Math.min(this.position.y, y + height));
      const dx = this.position.x - closestX;
      const dy = this.position.y - closestY;
      return Math.sqrt(dx * dx + dy * dy);
    }
  }

  const handleInteraction = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const newPos = {
      x: (e.touches ? e.touches[0].clientX : e.clientX) - rect.left,
      y: (e.touches ? e.touches[0].clientY : e.clientY) - rect.top
    };
    mousePosRef.current = newPos;
    updateMouseVelocity(newPos);
  };



  const updateMouseVelocity = (newPos) => {
    const currentTime = Date.now();
    const timeDelta = currentTime - lastMouseMoveTimeRef.current;
    
    if (timeDelta > 0) {
      mouseVelocityRef.current = {
        x: (newPos.x - prevMousePosRef.current.x) / timeDelta,
        y: (newPos.y - prevMousePosRef.current.y) / timeDelta
      };

      const velocityMagnitude = Math.sqrt(
        mouseVelocityRef.current.x * mouseVelocityRef.current.x + 
        mouseVelocityRef.current.y * mouseVelocityRef.current.y
      );

      isMouseMovingRef.current = velocityMagnitude > 0.05;
    }

    prevMousePosRef.current = { ...newPos };
    lastMouseMoveTimeRef.current = currentTime;

    setTimeout(() => {
      if (Date.now() - lastMouseMoveTimeRef.current > 100) {
        isMouseMovingRef.current = false;
      }
    }, 100);
  };

  

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const updateCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    // Initialize boids
    const numBoids = isMobile ? 500 : 1000;
    
    if (initialBoidsState) {
      boidsRef.current = initialBoidsState.map(boidState => {
        const boid = new Boid(
          boidState.position.x,
          boidState.position.y,
          window.innerWidth,
          window.innerHeight,
          boidState.isSpecial
        );
        boid.velocity = { ...boidState.velocity };
        boid.setPlayMode(isPlayMode);
        boid.setDeviceType(isMobile);
        return boid;
      });
    } else {
      boidsRef.current = Array.from({ length: numBoids }, (_, index) => {
        const boid = new Boid(
          Math.random() * window.innerWidth,
          Math.random() * window.innerHeight,
          window.innerWidth,
          window.innerHeight,
          index === 0
        );
        boid.setDeviceType(isMobile);
        return boid;
      });
    }

    const animate = () => {
        // Update gradient in play mode
        if (isPlayMode) {
          gradientTimeRef.current = (gradientTimeRef.current + 0.001) % 1;
          const color = interpolateColors(duskColors, gradientTimeRef.current);
          
          // Create gradient
          const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
          gradient.addColorStop(0, `rgb(${color.r}, ${color.g}, ${color.b})`);
          gradient.addColorStop(1, `rgb(${Math.max(0, color.r - 30)}, ${Math.max(0, color.g - 30)}, ${Math.max(0, color.b - 30)})`);
          
          ctx.fillStyle = gradient;
        } else {
          ctx.fillStyle = COLORS.background;
        }
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      
        if (!isPlayMode) {
          drawShape(ctx);
        }
      
        // Track the center of the flock
        let flockCenterX = 0;
        let flockCenterY = 0;
        let activeBoidsCount = 0;
      
        boidsRef.current.forEach(boid => {
          // Ensure device type and play mode are set
          boid.setDeviceType(isMobile);
          boid.setPlayMode(isPlayMode);
          
          boid.update(boidsRef.current, mousePosRef.current);
          boid.draw(ctx);
      
          // Only include visible boids in flock center calculation
          if (boid.position.x >= 0 && boid.position.x <= canvas.width &&
              boid.position.y >= 0 && boid.position.y <= canvas.height) {
            flockCenterX += boid.position.x;
            flockCenterY += boid.position.y;
            activeBoidsCount++;
          }
        });
      
        // Calculate average flock center using only visible boids
        if (activeBoidsCount > 0) {
          flockCenterX /= activeBoidsCount;
          flockCenterY /= activeBoidsCount;
      
          // Trigger notes in play mode based on flock center
          if (isPlayMode && tonalMatrixRef.current) {
            if (Math.random() < (isMobile ? 0.03 : 0.05)) { // Reduced trigger frequency on mobile
              tonalMatrixRef.current.triggerNote(
                flockCenterX, 
                flockCenterY, 
                canvas.width, 
                canvas.height
              );
            }
          }
        }
      
        // Update parent with current boid state
        onBoidsUpdate(boidsRef.current.map(boid => ({
          position: { ...boid.position },
          velocity: { ...boid.velocity },
          isSpecial: boid.isSpecial
        })));
      
        requestIdRef.current = requestAnimationFrame(animate);
      };

    animate();

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      if (requestIdRef.current) {
        cancelAnimationFrame(requestIdRef.current);
      }
    };
  }, [isPlayMode, initialBoidsState, isMobile, onBoidsUpdate]);

  useEffect(() => {
    if (isPlayMode) {
      tonalMatrixRef.current = new TonalMatrix();
      tonalMatrixRef.current.start();
      
      return () => {
        if (tonalMatrixRef.current) {
          tonalMatrixRef.current.cleanup();
          tonalMatrixRef.current = null;
        }
      };
    }
  }, [isPlayMode]);

  const handleChatbotInteractionCheck = (chatbotPosition) => {
    const interaction = boidsRef.current.some(boid => {
      const dx = boid.position.x - chatbotPosition.x;
      const dy = boid.position.y - chatbotPosition.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      return distance < 50; // Adjust interaction radius
    });

    setBoidInteraction(interaction); // Changed from setChatbotInteraction to setBoidInteraction
  };



  return (
    <>
      <canvas
        ref={canvasRef}
        onMouseMove={!isMobile ? handleInteraction : undefined}
        onTouchMove={isMobile ? handleInteraction : undefined}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: COLORS.background
        }}
      />
      
      {/* Add Chatbot Component */}
      <FestivalChatbot 
        isMobile={isMobile} 
        isPlayMode={isPlayMode}
        onInteractionCheck={handleChatbotInteractionCheck}
      />
    </>
  );
};

export default BoidSimulation;